import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import {
    ForecastValue,
    ForecastValueType
} from '../../components/ForecastValue/ForecastValue';
import { MarkedText } from '../../components/GridPlatformNameCell/GridPlatformNameCell';
import { MoneyText } from '../../components/MoneyText/MoneyText';
import { NumberText } from '../../components/NumberText/NumberText';
import { RecommendationsGridItemAllowCell } from '../../components/RecommendationsGridItemAllowCell/RecommendationsGridItemAllowCell';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { ColumnsSettings, ColumnsSettingsViewDict } from '../../typings/common';
import {
    CurrencyCode,
    GdRecommendationStatus
} from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnsOptions,
    GridColumnStaticDecl,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import {
    RecommendationsItemsScreenActions,
    RecommendationType,
    RemovePagesFromBlackListRecommendationsItem
} from '../../typings/recommendations';

import * as keyset from './removePagesFromBlackListRecommendations.i18n';

const i18n = i18nFactory(keyset);

export enum RemovePagesFromBlackListKey {
    CLICKS_FORECAST = 'CLICKS',
    COST_FORECAST = 'COST'
}

export function getStaticRemovePagesFromBlackListRecommendationsColumns(
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<
    OptionalGridColumnDecl<
        RemovePagesFromBlackListRecommendationsItem,
        {},
        GridColumnsOptions
    >
> {
    return [
        {
            title: i18n('name'),
            key: 'name',
            fixed: true,
            isSortable: false,
            defaultSortDirection: SortDirection.asc,
            minWidth: 150,
            CellComponent: React.memo(
                ({
                    rowData: { name },
                    rowIndex
                }: GridCellProps<
                    RemovePagesFromBlackListRecommendationsItem
                >): JSX.Element => <MarkedText name={name} />
            )
        },
        {
            title: i18n('clicks-forecast'),
            key: RemovePagesFromBlackListKey.CLICKS_FORECAST,
            align: 'right',
            isSortable: true,
            minWidth: 115,
            CellComponent: React.memo(
                ({
                    rowData: { clicksForecast },
                    rowIndex
                }: GridCellProps<
                    RemovePagesFromBlackListRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.positive}>
                        <NumberText
                            num={clicksForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        },
        {
            title: i18n('cost-forecast', {
                currency: mapStringToCurrencySymbol(currency)
            }),
            key: RemovePagesFromBlackListKey.COST_FORECAST,
            align: 'right',
            isSortable: true,
            minWidth: 120,
            CellComponent: React.memo(
                ({
                    rowData: { costForecast },
                    rowIndex
                }: GridCellProps<
                    RemovePagesFromBlackListRecommendationsItem
                >): JSX.Element => (
                    <ForecastValue type={ForecastValueType.neutral}>
                        <MoneyText
                            num={costForecast}
                            fractionalPartLength={0}
                        />
                    </ForecastValue>
                )
            )
        },
        {
            title: '',
            key: 'accept',
            isSortable: false,
            minWidth: 170,
            CellComponent: React.memo(
                ({
                    rowData: {
                        name,
                        id,
                        recommendationKeys,
                        status,
                        isApplicable
                    },
                    rowIndex
                }: GridCellProps<
                    RemovePagesFromBlackListRecommendationsItem
                >): JSX.Element => {
                    const { submitRecommendation } = actions;

                    return (
                        <RecommendationsGridItemAllowCell
                            name={name}
                            id={id}
                            isInProgress={
                                status === GdRecommendationStatus.IN_PROGRESS
                            }
                            isApplicable={isApplicable}
                            recommendationType={
                                RecommendationType.removePagesFromBlackList
                            }
                            recommendationKeys={recommendationKeys}
                            onSubmit={submitRecommendation}
                        />
                    );
                }
            )
        }
    ];
}

export function getDefaultDynamicRemovePagesFromBlackListRecommendationsColumns(): Pick<
    ColumnsSettings,
    'columnViewInfo'
> {
    return {
        columnViewInfo: {
            name: {
                width: 250
            },
            [RemovePagesFromBlackListKey.CLICKS_FORECAST]: {
                width: 160
            },
            [RemovePagesFromBlackListKey.COST_FORECAST]: {
                width: 190
            },
            accept: {
                width: 170
            }
        }
    };
}

export function getRemovePagesFromBlackListRecommendationsColumns(
    columnsSettings: ColumnsSettingsViewDict,
    actions: RecommendationsItemsScreenActions,
    login: string,
    currency: CurrencyCode
): Array<GridColumnStaticDecl<RemovePagesFromBlackListRecommendationsItem>> {
    return getStaticRemovePagesFromBlackListRecommendationsColumns(
        actions,
        login,
        currency
    ).map(column => {
        return {
            ...column,
            ...columnsSettings[RecommendationType.removePagesFromBlackList]
                .columnViewInfo[column.key]
        };
    });
}
